/**
 * Touch momentum / inertial scrolling utility.
 *
 * Provides exponential-decay deceleration after a touch-scroll gesture ends.
 * Calls a scroll callback with accumulated pixel deltas at each animation frame.
 * Pure utility -- no DOM dependencies beyond rAF and performance.now.
 */

export interface MomentumConfig {
  /** Friction coefficient (0-1). Higher = slower deceleration. Default: 0.88 */
  friction?: number
  /** Minimum velocity to continue animation (px/ms). Default: 0.08 */
  minVelocity?: number
  /** Maximum initial velocity cap (px/ms). Default: 3.0 */
  maxVelocity?: number
}

export interface MomentumState {
  /** Cancel the running animation */
  cancel: () => void
  /** Whether the animation is currently active */
  isActive: () => boolean
}

/**
 * Start a momentum scroll animation.
 *
 * @param initialVelocity - Velocity at finger lift (px/ms, positive = scroll up)
 * @param onScroll - Called each frame with pixel delta to scroll
 * @param onEnd - Called when momentum animation completes naturally
 * @param config - Physics tuning parameters
 */
export function startMomentum(
  initialVelocity: number,
  onScroll: (pixelDelta: number) => void,
  onEnd: () => void,
  config?: MomentumConfig,
): MomentumState {
  const friction = config?.friction ?? 0.88
  const minVelocity = config?.minVelocity ?? 0.08
  const maxVelocity = config?.maxVelocity ?? 3.0

  // Clamp to [-maxVelocity, maxVelocity]
  let velocity = Math.sign(initialVelocity) * Math.min(Math.abs(initialVelocity), maxVelocity)
  let rafId: number | null = null
  let lastTime = performance.now()
  let active = true

  // If velocity is already below threshold, end immediately
  if (Math.abs(velocity) < minVelocity) {
    active = false
    onEnd()
    return { cancel: () => {}, isActive: () => false }
  }

  const tick = () => {
    if (!active) return
    const now = performance.now()
    const dt = now - lastTime
    lastTime = now

    velocity *= friction

    if (Math.abs(velocity) < minVelocity) {
      active = false
      rafId = null
      onEnd()
      return
    }

    const delta = velocity * dt
    onScroll(delta)
    rafId = requestAnimationFrame(tick)
  }

  rafId = requestAnimationFrame(tick)

  return {
    cancel: () => {
      active = false
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
    },
    isActive: () => active,
  }
}
